import React, { forwardRef, useEffect } from "react";
import { useApi } from "src/Api/useApi";
import {
	NeurapulseGetProjects,
	NeurapulseProject,
} from "src/@types/ImpiricaPlatformApi";
import { LoadingIndicator } from "./LoadingIndicator";
import clsx from "clsx";
import styles from "./NeurapulseProjectSelector.module.css";
import { Select } from "antd";

type NeurapulseSelectorProps = {
	apiKey: string;
	value?: string; // injected by antd Form.getFieldDecorator
	onChange?: (value: string) => void; // injected by antd Form.getFieldDecorator
};

export const NeurapulseProjectSelector = forwardRef<
	HTMLDivElement,
	NeurapulseSelectorProps
>(({ apiKey, value, onChange }, ref) => {
	const { neurapulseClient } = useApi();
	const [projects, setProjects] = React.useState<NeurapulseProject[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	useEffect(() => {
		const fetchProjects = async () => {
			if (apiKey && neurapulseClient) {
				setLoading(true);
				setError(null);
				try {
					const p = await neurapulseClient.getNeurapulseProjectsPOST(
						new NeurapulseGetProjects({
							apiKey,
						})
					);
					setProjects(p);
				} catch (e: any) {
					console.error("Error fetching Neurapulse project:", e);
					setError("Failed to load project");
				} finally {
					setLoading(false);
				}
			}
		};
		fetchProjects();
	}, [apiKey, neurapulseClient]);
	// Fetch and display project details using projectId

	const handleSelectChange = (val: string) => {
		// propagate to antd form
		onChange && onChange(val);
	};

	return (
		<div ref={ref} className={styles.selectorContainer}>
			{loading && <LoadingIndicator />}
			{!loading && error && (
				<div className={clsx(styles.error)} role="alert">
					{error}
				</div>
			)}
			{!loading && !error && projects && (
				<Select
					placeholder="Select a Neurapulse Project"
					className={styles.projectSelect}
					value={value}
					onChange={handleSelectChange}
				>
					<Select.Option value="">-- Create a new project --</Select.Option>
					{projects.map((project) => (
						<Select.Option key={project.id} value={project.id}>
							{`${project.name} (${project.dateCreated.toLocaleDateString()})`}
						</Select.Option>
					))}
				</Select>
			)}
		</div>
	);
});
